import { useCallback, useEffect, useMemo, useState } from "react"
import { useDispatch } from "react-redux"
import SectionTitle from "./SectionTitle"
import customAxios from "../../../../utils/customAxios";
import ConfirmationModal from "../../components/ConfirmationModal"
import { getBooking } from "../../../../actions/admin/booking"
import VehicleAssignmentField from "./assignment/VehicleAssignmentField"
import DriverAssignmentField from "./assignment/DriverAssignmentField"
import MeetAssistStaffField from "./assignment/MeetAssistStaffField"
import {
    assignmentFromSlot,
    assignmentSignature,
    describeAssignment,
    emptyAssignment,
    hasIncompleteMeetAssistStaff,
    hasMeetAssistService,
    idOf,
    isAssignmentFilled,
    meetAssistServiceLabel,
    meetAssistStaffsFromBooking,
    meetAssistStaffsSignature,
    toAssignmentPayload,
    toMeetAssistStaffsPayload,
} from "./assignment/assignmentUtils"

const AssignedVehicleDetail = ({
    booking
}) => {
    const dispatch = useDispatch();

    const [vehicleOptions, setVehicleOptions] = useState([]);
    const [driverOptions, setDriverOptions] = useState([]);
    // One entry per vehicle slot, in bookingInfo order.
    const [assignments, setAssignments] = useState([]);
    // Meet & assist is worked per booking rather than per vehicle, so its staff
    // sit beside the slots instead of inside one.
    const [meetAssistStaffs, setMeetAssistStaffs] = useState([]);
    const [showConfirmation, setShowConfirmation] = useState(false);
    const [isAssigning, setIsAssigning] = useState(false);
    const [assignError, setAssignError] = useState("");
    const [assignSuccess, setAssignSuccess] = useState("");
    // { enabled, vehicles: { id: {bookingKey,...} }, drivers: { ... } } — which
    // vehicles/drivers are already committed to an overlapping booking.
    const [conflicts, setConflicts] = useState({ enabled: false, vehicles: {}, drivers: {} });

    useEffect(() => {
        const fetchAssignmentOptions = async () => {
            try {
                const [vehiclesResponse, driversResponse] = await Promise.all([
                    customAxios.get("/api/admin/vehicles"),
                    customAxios.get("/api/admin/drivers"),
                ]);

                const mappedVehicles = (vehiclesResponse?.data || [])
                    .filter((item) => item?.isactive !== false)
                    .map((item) => ({
                        value: item?._id,
                        label: item?.vehiclename || "-",
                        vehicleName: item?.vehiclename || "-",
                        vehicleType: item?.vehicletype || "-",
                        registrationNumber: item?.registration || "-",
                        vehicleTypeId: idOf(item?.vehicletypeid),
                    }));

                const mappedDrivers = (driversResponse?.data || [])
                    .filter((item) => item?.isactive !== false)
                    .map((item) => ({
                        value: item?._id,
                        label: item?.name || item?.email || "-",
                        driverName: item?.name || "-",
                        driverPhone: item?.phone || "-",
                        driverPrefix: item?.prefix_number || item?.prefixPhoneNumber || "",
                        driverEmail: item?.email || "-",
                    }));

                setVehicleOptions(mappedVehicles);
                setDriverOptions(mappedDrivers);
            } catch (error) {
                setVehicleOptions([]);
                setDriverOptions([]);
            }
        };

        fetchAssignmentOptions();
    }, []);

    const fetchConflicts = useCallback(async () => {
        if (!booking?._id) return;
        try {
            const response = await customAxios.get(`/api/admin/bookings/assignment-conflicts/${booking._id}`);
            setConflicts(response?.data || { enabled: false, vehicles: {}, drivers: {} });
        } catch (error) {
            // The server re-checks on submit, so a failure here only costs the hint.
            setConflicts({ enabled: false, vehicles: {}, drivers: {} });
        }
    }, [booking?._id]);

    useEffect(() => {
        fetchConflicts();
    }, [fetchConflicts]);

    const bookingInfo = useMemo(
        () => (Array.isArray(booking?.bookingInfo) ? booking.bookingInfo : []),
        [booking]
    );
    // A booking with no vehicle slots still gets one row to assign against.
    const slots = useMemo(() => (bookingInfo.length ? bookingInfo : [{}]), [bookingInfo]);

    const savedAssignments = useMemo(() => slots.map(assignmentFromSlot), [slots]);

    useEffect(() => {
        setAssignments(savedAssignments);
    }, [savedAssignments]);

    // Only bookings that bought meet & assist — regular or VIP — get the form.
    const needsMeetAssistStaff = hasMeetAssistService(booking);
    const meetAssistLabel = meetAssistServiceLabel(booking);
    const savedMeetAssistStaffs = useMemo(() => meetAssistStaffsFromBooking(booking), [booking]);

    useEffect(() => {
        setMeetAssistStaffs(savedMeetAssistStaffs);
    }, [savedMeetAssistStaffs]);

    const assignmentRows = slots.map((item, index) => ({
        key: item?._id || item?.vehicleTypeID?._id || `assignment-${index}`,
        index,
        vehicleName: item?.vehicleTypeName || item?.vehicleTypeID?.typename || item?.vehicleID?.vehiclename || "-",
        vehicleTypeID: item?.vehicleTypeID,
    }));

    // react-select honours `isDisabled` per option; the label carries the reason
    // so a greyed-out row explains itself.
    const withConflicts = (options, conflictMap, currentIds) =>
        options.map((option) => {
            const clash = conflictMap?.[option.value];
            // Whatever is already saved on this booking must stay selectable.
            const isOwn = currentIds.includes(option.value);

            return clash && !isOwn
                ? {
                    ...option,
                    isDisabled: true,
                    label: `${option.label} — busy on #${clash.bookingKey}`,
                }
                : option;
        });

    const savedIds = {
        vehicles: savedAssignments.map((item) => item.vehicle).filter(Boolean),
        drivers: savedAssignments.map((item) => item.driver).filter(Boolean),
    };

    const availableVehicleOptions = withConflicts(vehicleOptions, conflicts.vehicles, savedIds.vehicles);
    const availableDriverOptions = withConflicts(driverOptions, conflicts.drivers, savedIds.drivers);

    const menuPortalTarget = typeof window !== "undefined" ? document.body : null;

    // Every slot needs a vehicle and a settled driver — where "settled" includes
    // deciding the client brings their own. Meet & assist staff are not required
    // to assign the vehicles: they are often known later, and holding the whole
    // booking back for them would help nobody. A half-typed staff row is the one
    // exception, since it would be saved as a person with no name.
    const isComplete =
        assignments.length > 0 &&
        assignments.every(isAssignmentFilled) &&
        !(needsMeetAssistStaff && hasIncompleteMeetAssistStaff(meetAssistStaffs));
    const hasChanges =
        assignments.some(
            (assignment, index) => assignmentSignature(assignment) !== assignmentSignature(savedAssignments[index] || emptyAssignment())
        ) ||
        (needsMeetAssistStaff &&
            meetAssistStaffsSignature(meetAssistStaffs) !== meetAssistStaffsSignature(savedMeetAssistStaffs));
    const isAlreadyAssigned = savedAssignments.every(isAssignmentFilled);

    const patchAssignment = (index, patch) => {
        setAssignments((current) =>
            current.map((assignment, position) => (position === index ? { ...assignment, ...patch } : assignment))
        );
        setAssignSuccess("");
    };

    const changeMeetAssistStaffs = (next) => {
        setMeetAssistStaffs(next);
        setAssignSuccess("");
    };

    // Blank rows are dropped, so the modal shows the people who will actually be
    // saved rather than the shape of the form.
    const confirmationStaffs = needsMeetAssistStaff ? toMeetAssistStaffsPayload(meetAssistStaffs) : [];

    const confirmationRows = assignmentRows.map((row, index) => ({
        key: row.key,
        order: index + 1,
        vehicleTypeName: row.vehicleName,
        ...describeAssignment(assignments[index] || emptyAssignment(), vehicleOptions, driverOptions),
    }));

    const handleAssign = async () => {
        setIsAssigning(true);
        setAssignError("");

        try {
            await customAxios.post("/api/admin/bookings/assign-vehicle-driver", {
                bookingId: booking?._id,
                assignments: assignments.map(toAssignmentPayload),
                // Sent only where it means something; the server ignores staff on a
                // booking without the service either way.
                meetAssistStaffs: needsMeetAssistStaff ? toMeetAssistStaffsPayload(meetAssistStaffs) : undefined,
            });

            setShowConfirmation(false);
            setAssignSuccess(
                "Vehicle and driver assigned. The admin and the customer have been notified by email."
            );
            // Pull the booking back so the section reflects what was actually saved,
            // and re-scan since this assignment now occupies the slot itself.
            dispatch(getBooking(booking?._id));
            fetchConflicts();
        } catch (error) {
            setAssignError(
                error?.response?.data?.msg || "Could not assign the vehicle and driver. Please try again."
            );
        } finally {
            setIsAssigning(false);
        }
    };

    return (
        <>
            <SectionTitle title="Vehicle & Driver Assignment" />
            <div className="tw:mb-5 tw:overflow-hidden tw:rounded-[5px] tw:border tw:border-[#E8E8E8] tw:bg-white">
                {assignmentRows.map((row, index) => {
                    const assignment = assignments[index];
                    if (!assignment) return null;

                    return (
                        <div key={row.key}>
                            <div className="tw:p-4 tw:bg-[#F9F9F9] tw:border tw:border-[#E8E8E8]">
                                <div className="tw:font-hanken-grotesk tw:text-[13px] tw:font-[400] tw:text-[#8D8D8D]">{`Vehicle #${index + 1}`}</div>
                                <div className="tw:font-hanken-grotesk tw:text-[15px] tw:font-[400] tw:leading-[38px] tw:text-[#282828]">
                                    {row.vehicleName}
                                </div>
                            </div>
                            <div className="tw:h-px tw:w-full tw:bg-[#E8E8E8]" />

                            <div className="tw:px-4 tw:py-6">
                                <VehicleAssignmentField
                                    index={index}
                                    assignment={assignment}
                                    options={availableVehicleOptions}
                                    bookedVehicleTypeId={row.vehicleTypeID}
                                    menuPortalTarget={menuPortalTarget}
                                    onPatch={(patch) => patchAssignment(index, patch)}
                                />

                                <div className="tw:my-6 tw:h-px tw:w-full tw:bg-[#E8E8E8]" />

                                <DriverAssignmentField
                                    index={index}
                                    assignment={assignment}
                                    options={availableDriverOptions}
                                    menuPortalTarget={menuPortalTarget}
                                    onPatch={(patch) => patchAssignment(index, patch)}
                                />

                            </div>
                            {index < assignmentRows.length - 1 ? <div className="tw:h-px tw:w-full tw:bg-[#E8E8E8]" /> : null}
                        </div>
                    );
                })}

                {needsMeetAssistStaff ? (
                    <>
                        <div className="tw:h-px tw:w-full tw:bg-[#E8E8E8]" />
                        <MeetAssistStaffField
                            serviceLabel={meetAssistLabel}
                            staffs={meetAssistStaffs}
                            isIncomplete={hasIncompleteMeetAssistStaff(meetAssistStaffs)}
                            onChange={changeMeetAssistStaffs}
                        />
                    </>
                ) : null}

                <div className="tw:h-px tw:w-full tw:bg-[#E8E8E8]" />
                <div className="tw:flex tw:flex-wrap tw:items-center tw:justify-end tw:gap-3 tw:px-4 tw:py-5">
                    {assignError ? (
                        <span className="tw:mr-auto tw:font-hanken-grotesk tw:text-[14px] tw:!text-[#EA4335]">
                            {assignError}
                        </span>
                    ) : assignSuccess ? (
                        <span className="tw:mr-auto tw:font-hanken-grotesk tw:text-[14px] tw:!text-[#3CAEA3]">
                            {assignSuccess}
                        </span>
                    ) : null}

                    <button
                        type="button"
                        disabled={!isComplete || !hasChanges || isAssigning}
                        onClick={() => {
                            setAssignError("");
                            setShowConfirmation(true);
                        }}
                        className={`tw:!rounded-md tw:!border-0 tw:!px-10 tw:!py-3 tw:!font-hanken-grotesk tw:!text-[15px] tw:!text-white ${
                            !isComplete || !hasChanges || isAssigning
                                ? "tw:!bg-[#D9D9D9] tw:!cursor-not-allowed"
                                : "tw:!bg-[#DBBE78] tw:!cursor-pointer"
                        }`}
                    >
                        {isAlreadyAssigned ? "Update Assignment" : "Assign"}
                    </button>
                </div>
            </div>

            <ConfirmationModal
                show={showConfirmation}
                size="modal-lg"
                title={isAlreadyAssigned
                    ? "Update the vehicle and driver for this booking?"
                    : "Assign this vehicle and driver to the booking?"}
                description="The admin and the customer will be notified by email."
                error={assignError}
                confirmLabel="Confirm"
                busyLabel="Assigning…"
                isBusy={isAssigning}
                onCancel={() => setShowConfirmation(false)}
                onConfirm={handleAssign}
            >
                <div className="tw:flex tw:flex-col tw:gap-3">
                    {confirmationRows.map((row) => (
                        <div
                            key={row.key}
                            className="tw:rounded-[5px] tw:border tw:border-[#E8E8E8] tw:bg-[#F9F9F9] tw:px-5 tw:py-4"
                        >
                            <div className="tw:mb-2 tw:font-hanken-grotesk tw:text-[13px] tw:!text-[#8D8D8D]">
                                {`Vehicle #${row.order} — ${row.vehicleTypeName}`}
                            </div>
                            <div className="tw:grid tw:grid-cols-1 tw:gap-2 tw:md:grid-cols-2">
                                <div>
                                    <div className="tw:font-hanken-grotesk tw:text-[14px] tw:!text-[#282828]">
                                        {row.vehicleName}
                                        {row.isCustomVehicle ? (
                                            <span className="tw:!text-[#A3780F]"> · not in fleet</span>
                                        ) : row.isFreeUpgrade ? (
                                            <span className="tw:!text-[#A3780F]"> · free upgrade</span>
                                        ) : null}
                                    </div>
                                    <div className="tw:font-hanken-grotesk tw:text-[13px] tw:!text-[#666666]">
                                        {row.registrationNumber}
                                    </div>
                                </div>
                                <div>
                                    <div className="tw:font-hanken-grotesk tw:text-[14px] tw:!text-[#282828]">
                                        {row.driverName}
                                        {row.isCustomDriver ? (
                                            <span className="tw:!text-[#A3780F]"> · not in fleet</span>
                                        ) : null}
                                    </div>
                                    <div className="tw:font-hanken-grotesk tw:text-[13px] tw:!text-[#666666]">
                                        {row.driverDetail}
                                    </div>
                                </div>
                            </div>
                        </div>
                    ))}

                    {confirmationStaffs.length ? (
                        <div className="tw:rounded-[5px] tw:border tw:border-[#E8E8E8] tw:bg-[#F9F9F9] tw:px-5 tw:py-4">
                            <div className="tw:mb-2 tw:font-hanken-grotesk tw:text-[13px] tw:!text-[#8D8D8D]">
                                {`Meet Service Staff — ${meetAssistLabel || "Meet & Assist"}`}
                            </div>
                            <div className="tw:grid tw:grid-cols-1 tw:gap-2 tw:md:grid-cols-2">
                                {confirmationStaffs.map((staff, index) => (
                                    <div key={`confirm-staff-${index}`}>
                                        <div className="tw:font-hanken-grotesk tw:text-[14px] tw:!text-[#282828]">
                                            {staff.name}
                                        </div>
                                        <div className="tw:font-hanken-grotesk tw:text-[13px] tw:!text-[#666666]">
                                            {staff.phone || "No phone number"}
                                        </div>
                                    </div>
                                ))}
                            </div>
                        </div>
                    ) : null}
                </div>
            </ConfirmationModal>
        </>
    );
};

export default AssignedVehicleDetail;
